import { CheckCheck, Image, Video, FileText, Phone, ExternalLink, Reply } from 'lucide-react';

interface WhatsAppPreviewProps {
  headerFormat?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
  headerText?: string;
  headerExample?: string;
  headerMediaUrl?: string;
  bodyText: string;
  bodyExamples?: string[];
  footerText?: string;
  buttons?: Array<{
    type: 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL';
    text: string;
    phone_number?: string;
    url?: string;
  }>;
  /** Quando true, destaca variáveis: com exemplo em azul + tooltip, sem exemplo em badge roxo */
  highlightVariables?: boolean;
}

export default function WhatsAppPreview({
  headerFormat,
  headerText,
  headerExample,
  headerMediaUrl,
  bodyText,
  bodyExamples = [],
  footerText,
  buttons = [],
  highlightVariables = false,
}: WhatsAppPreviewProps) {
  // Aplica formatação WhatsApp (bold, italic, etc) — NÃO mexe em variáveis
  const formatWhatsAppText = (text: string): string => {
    if (!text) return text;
    text = text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    text = text.replace(/_([^_]+)_/g, '<em>$1</em>');
    text = text.replace(/~([^~]+)~/g, '<del>$1</del>');
    text = text.replace(/```([^`]+)```/g, '<code class="bg-gray-100 px-1 rounded font-mono text-xs">$1</code>');
    return text;
  };

  // Gera HTML para variável com exemplo (azul + tooltip instantâneo via CSS)
  const styledVariable = (num: string, example: string): string =>
    `<span style="color:#2563EB;font-weight:600;border-bottom:2px dashed #93C5FD;cursor:help;padding-bottom:1px;position:relative;" onmouseenter="this.querySelector('.vtip').style.opacity='1';this.querySelector('.vtip').style.visibility='visible'" onmouseleave="this.querySelector('.vtip').style.opacity='0';this.querySelector('.vtip').style.visibility='hidden'">{{${example}}}<span class="vtip" style="position:absolute;bottom:100%;left:50%;transform:translateX(-50%);margin-bottom:4px;background:#1e293b;color:#fff;font-size:10px;padding:2px 8px;border-radius:4px;white-space:nowrap;pointer-events:none;opacity:0;visibility:hidden;transition:opacity 0.1s;z-index:50;">Variável {{${num}}}</span></span>`;

  // Gera HTML para variável sem exemplo (badge roxo)
  const badgeVariable = (num: string): string =>
    `<span style="background:#8B5CF6;color:#fff;padding:1px 6px;border-radius:4px;font-size:11px;font-weight:600;font-family:monospace;white-space:nowrap;">{{${num}}}</span>`;

  /**
   * Processa texto com variáveis de forma segura:
   * 1. Substitui {{N}} por placeholders seguros
   * 2. Aplica formatação WhatsApp (sem risco de corromper HTML)
   * 3. Troca placeholders pelo HTML final estilizado
   */
  const processText = (text: string, examples: Array<{ index: number; value: string }>): string => {
    if (!text) return text;

    const placeholders: Record<string, string> = {};

    // Passo 1: trocar {{N}} por placeholders seguros
    const withPlaceholders = text.replace(/\{\{(\d+)\}\}/g, (_match, num) => {
      const placeholder = `XVARX${num}XVARX`;
      const example = examples.find(e => e.index === parseInt(num));

      if (highlightVariables) {
        placeholders[placeholder] = example
          ? styledVariable(num, example.value)
          : badgeVariable(num);
      } else {
        placeholders[placeholder] = example ? example.value : `{{${num}}}`;
      }

      return placeholder;
    });

    // Passo 2: aplicar formatação WhatsApp no texto limpo
    let formatted = formatWhatsAppText(withPlaceholders);

    // Passo 3: substituir placeholders pelo HTML final
    for (const [ph, html] of Object.entries(placeholders)) {
      formatted = formatted.split(ph).join(html);
    }

    return formatted;
  };

  const renderHeader = () => {
    if (!headerText) return null;
    const examples = headerExample ? [{ index: 1, value: headerExample }] : [];
    return processText(headerText, examples);
  };

  const renderBody = () => {
    const text = bodyText || 'Digite o corpo da mensagem...';
    const examples = bodyExamples
      .map((value, i) => (value ? { index: i + 1, value } : null))
      .filter((e): e is { index: number; value: string } => e !== null);
    return processText(text, examples);
  };

  const renderFooter = () => formatWhatsAppText(footerText || '');

  const renderMediaIcon = () => {
    switch (headerFormat) {
      case 'IMAGE': return <Image size={20} className="text-gray-400" />;
      case 'VIDEO': return <Video size={20} className="text-gray-400" />;
      case 'DOCUMENT': return <FileText size={20} className="text-gray-400" />;
      default: return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-[#075E54] text-white p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium">
          C
        </div>
        <div className="flex-1">
          <div className="font-medium">Cliente</div>
          <div className="text-xs text-green-200">online</div>
        </div>
      </div>

      <div
        className="flex-1 p-4 overflow-y-auto"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23e5ddd5\'/%3E%3Cpath d=\'M20 20h60v60H20z\' fill=\'none\' stroke=\'%23d1c7ba\' stroke-width=\'0.5\' opacity=\'0.1\'/%3E%3C/svg%3E")',
          backgroundColor: '#e5ddd5'
        }}
      >
        <div className="flex justify-end mb-2">
          <div className="max-w-[85%]">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {headerFormat && headerFormat !== 'TEXT' && (
                <div className="bg-gray-100">
                  {headerFormat === 'IMAGE' && headerMediaUrl ? (
                    <img src={headerMediaUrl} alt="Header" className="w-full h-auto" onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }} />
                  ) : null}
                  <div className={`p-8 flex items-center justify-center ${headerFormat === 'IMAGE' && headerMediaUrl ? 'hidden' : ''}`}>
                    {renderMediaIcon()}
                    <span className="ml-2 text-sm text-gray-500">
                      {headerFormat === 'IMAGE' && 'Imagem'}
                      {headerFormat === 'VIDEO' && 'Vídeo'}
                      {headerFormat === 'DOCUMENT' && 'Documento'}
                    </span>
                  </div>
                </div>
              )}

              {headerFormat === 'TEXT' && headerText && (
                <div className="px-3 pt-3">
                  <div
                    className="font-bold text-gray-900 text-sm"
                    dangerouslySetInnerHTML={{ __html: renderHeader() || '' }}
                  />
                </div>
              )}

              <div className="px-3 py-2">
                <div
                  className="text-gray-900 text-sm whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: renderBody() }}
                />
              </div>

              {footerText && (
                <div className="px-3 pb-2">
                  <div
                    className="text-xs text-gray-500"
                    dangerouslySetInnerHTML={{ __html: renderFooter() }}
                  />
                </div>
              )}

              <div className="px-3 pb-2 flex items-center justify-end gap-1">
                <span className="text-[10px] text-gray-500">12:34</span>
                <CheckCheck size={14} className="text-blue-500" />
              </div>

              {buttons.length > 0 && (
                <div className="border-t border-gray-200">
                  {buttons.map((button, index) => (
                    <button
                      key={index}
                      className="w-full px-4 py-3 text-sm text-[#075E54] hover:bg-gray-50 flex items-center justify-center gap-2 border-b border-gray-200 last:border-b-0 transition-colors"
                    >
                      {button.type === 'PHONE_NUMBER' && <Phone size={14} />}
                      {button.type === 'URL' && <ExternalLink size={14} />}
                      {button.type === 'QUICK_REPLY' && <Reply size={14} />}
                      <span className="font-medium">
                        {button.text || `Botão ${index + 1}`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-xs text-gray-600">
            Preview em tempo real
          </div>
        </div>
      </div>
    </div>
  );
}
